import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { AzureLoginService } from 'src/app/services/azure-login.service';
import { dateTimeFormatterLocal } from '../../shared/utilities/date-utilities';
import { TabsService } from '../../services/tabs.service';
import { Tab } from '../../services/tab';
import { SearchContractService } from '../search-contract/search-contract.service';
import {
  IContractPortalLinkHistoryItem,
  IContractPortalLinkedContract,
  IContractPortalUser,
  IContractPortalUserProfile,
  IContractPortalUsersAssociationStatus,
} from '../up-tab/up-tab.interface';
import { UserProfileTabService } from './user-profile-tab.service';

type UserProfileView = 'email' | 'contract';
type ProfileDetailTab = 'contracts' | 'link' | 'history';

@Component({
  standalone: false,
  selector: 'user-profile-tab',
  templateUrl: 'user-profile-tab.template.html',
  styleUrls: ['user-profile-tab.scss'],
  providers: [UserProfileTabService, SearchContractService, MessageService],
})
export class UserProfileTabComponent implements OnDestroy {
  activeView: UserProfileView = 'email';
  detailTab: ProfileDetailTab = 'contracts';

  emailSearch = '';
  contractSearch = '';
  linkContractNumber = '';
  linkContractMatch = '';
  linkContractError = '';
  linkContractStatus = '';
  linkAssociation: IContractPortalUsersAssociationStatus | null = null;
  readonly inactiveContractLinkMessage =
    'You cannot link a profile to this contract because it is no longer active.';
  // Tracks the exact text that was last searched, since linkContractMatch
  // may hold a differently-formatted SCM value (e.g. an "NL"-prefixed
  // contract number) than what the user actually typed.
  private lastSearchedLinkContractNumber = '';
  private linkLookupRequestId = 0;

  searchResults: IContractPortalUserProfile[] = [];
  searchResultCount = 0;
  selectedUser: IContractPortalUserProfile | null = null;
  linkedContracts: IContractPortalLinkedContract[] = [];

  contractUsers: IContractPortalUser[] = [];
  activeContractNumber = '';

  isSearchingUsers = false;
  isLoadingLinkedContracts = false;
  isLinkingContract = false;
  isSearchingLinkContract = false;
  isSearchingContractUsers = false;
  isLinkActionInProgress = false;
  pendingLinkKey = '';

  // Inline history (shown inside the selected profile detail)
  historyItems: IContractPortalLinkHistoryItem[] = [];
  isLoadingHistory = false;

  // Dialog history (shown from the Search by Contract view) - kept separate
  // so opening the dialog never clears the selected profile's inline history.
  displayHistory = false;
  historyUser: IContractPortalUserProfile | IContractPortalUser | null = null;
  dialogHistoryItems: IContractPortalLinkHistoryItem[] = [];
  isLoadingDialogHistory = false;

  openingContractNumber = '';

  private subscriptions: Subscription[] = [];

  constructor(
    private userProfileTabService: UserProfileTabService,
    private searchContractService: SearchContractService,
    private tabsService: TabsService,
    private messageService: MessageService,
    private azureService: AzureLoginService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  get linkedContractProfiles(): IContractPortalUser[] {
    return this.contractUsers.filter((user) => this.isUserLinked(user));
  }

  get visibleSearchResults(): IContractPortalUserProfile[] {
    return this.searchResults;
  }

  get isLinkContractAlreadyLinked(): boolean {
    if (!this.linkContractMatch) {
      return false;
    }

    const matchedContractNumber = this.normalizeContractNumber(this.linkContractMatch);
    return this.linkedContracts.some(
      (contract) => this.normalizeContractNumber(contract.contractNumber) === matchedContractNumber,
    );
  }

  get isLinkBlockedByAssociation(): boolean {
    if (!this.linkContractMatch || !this.linkAssociation) {
      return false;
    }
    return this.linkAssociation.associated === true || this.linkAssociation.canLink === false;
  }

  get isSearchedContractInactive(): boolean {
    const status = (this.linkContractStatus || '').trim().toLowerCase();
    return !!status && status !== 'active';
  }

  get canOfferContractLink(): boolean {
    return (
      !!this.linkContractMatch &&
      !this.isSearchedContractInactive &&
      !this.isSearchingLinkContract &&
      !!this.linkAssociation &&
      this.linkAssociation.associated === false &&
      this.linkAssociation.canLink === true &&
      !this.isLinkContractAlreadyLinked
    );
  }

  get linkAssociationMessage(): string {
    return (
      this.linkAssociation?.message?.trim() ||
      'This contract is already associated with an account.'
    );
  }

  get linkAssociationUsers(): IContractPortalUser[] {
    return this.linkAssociation?.linkedUsers ?? [];
  }

  setView(view: UserProfileView): void {
    this.activeView = view;
  }

  setDetailTab(tab: ProfileDetailTab): void {
    this.detailTab = tab;
  }

  onSearchByEmail(): void {
    const email = this.emailSearch?.trim();
    if (!email) {
      this.showWarning('Please enter an email address to search.');
      return;
    }

    this.isSearchingUsers = true;
    this.selectedUser = null;
    this.linkedContracts = [];
    this.resetLinkContractLookup();

    this.subscriptions.push(
      this.userProfileTabService
        .searchPortalUserProfiles(email, this.azureService.accountId)
        .subscribe({
          next: ({ items, count }) => {
            this.searchResults = this.normalizePortalUserProfiles(items);
            this.searchResultCount = count ?? this.searchResults.length;
            this.isSearchingUsers = false;
            if (this.searchResults.length === 1) {
              this.onSelectUser(this.searchResults[0]);
            }
            this.cdr.detectChanges();
          },
          error: (err) => {
            this.isSearchingUsers = false;
            this.showError(this.getGraphQLErrorMessage(err, 'Failed to search user profiles.'));
          },
        }),
    );
  }

  onSelectUser(user: IContractPortalUserProfile): void {
    this.selectedUser = user;
    this.detailTab = 'contracts';
    this.resetLinkContractLookup();
    this.loadLinkedContracts(user);
    this.loadHistory(user);
  }

  onLinkContractNumberChange(): void {
    const contractNumber = this.linkContractNumber?.trim();
    if (contractNumber !== this.lastSearchedLinkContractNumber) {
      this.linkContractMatch = '';
      this.linkContractStatus = '';
      this.linkContractError = '';
      this.linkAssociation = null;
      this.isSearchingLinkContract = false;
      this.linkLookupRequestId += 1;
    }
  }

  onSearchLinkContract(): void {
    const contractNumber = this.linkContractNumber?.trim();
    if (!this.selectedUser) {
      this.showWarning('Select a user profile first.');
      return;
    }
    if (!contractNumber) {
      this.showWarning('Enter a contract number to search.');
      return;
    }

    this.isSearchingLinkContract = true;
    this.linkContractMatch = '';
    this.linkContractStatus = '';
    this.linkContractError = '';
    this.linkAssociation = null;
    this.lastSearchedLinkContractNumber = contractNumber;
    const requestId = ++this.linkLookupRequestId;

    // SCM stores some contract numbers with an "NL" prefix, so try both the
    // number as shown and the NL-prefixed form before giving up.
    const candidates = /^nl/i.test(contractNumber)
      ? [contractNumber, contractNumber.replace(/^nl/i, '')]
      : [contractNumber, `NL${contractNumber}`];

    this.resolveLinkContractCandidate(contractNumber, candidates, requestId);
  }

  private resolveLinkContractCandidate(
    displayNumber: string,
    candidates: string[],
    requestId: number,
    statuses: string[] = ['', 'Terminated'],
  ): void {
    const [current, ...remaining] = candidates;

    if (requestId !== this.linkLookupRequestId) {
      return;
    }

    if (!current) {
      this.isSearchingLinkContract = false;
      this.linkContractError = `Contract ${displayNumber} does not exist in SCM.`;
      this.cdr.detectChanges();
      return;
    }

    const [status = '', ...remainingStatuses] = statuses;

    this.subscriptions.push(
      this.searchContractService
        .getContractSearchResult({
          // Blank status matches the normal contract search default. Cancelled
          // contracts are stored as Terminated and are only returned when that
          // status is requested explicitly.
          status: { key: status, value: status },
          contractNumber: current,
          limit: 10,
          offset: 0,
          azureUserId: this.azureService.accountId,
        })
        .pipe(
          filter((result: any) => !result?.loading && !!result?.data),
          take(1),
        )
        .subscribe({
          next: ({ data }) => {
            if (requestId !== this.linkLookupRequestId) {
              return;
            }

            const rows = data?.getContractSearchResults ?? [];
            const match = this.pickContractMatch(rows, displayNumber, current);

            if (!match) {
              if (remainingStatuses.length) {
                this.resolveLinkContractCandidate(
                  displayNumber,
                  candidates,
                  requestId,
                  remainingStatuses,
                );
                return;
              }
              // Try the next candidate form (e.g. NL-prefixed) before giving up.
              this.resolveLinkContractCandidate(displayNumber, remaining, requestId);
              return;
            }

            const matchedContractNumber = match.contractNumber || displayNumber;
            this.linkContractStatus = (match.status || '').trim();
            if (this.isSearchedContractInactive) {
              this.linkContractMatch = matchedContractNumber;
              this.isSearchingLinkContract = false;
              this.linkAssociation = null;
              this.cdr.detectChanges();
              return;
            }
            this.loadLinkAssociationStatus(matchedContractNumber, requestId);
          },
          error: (err) => {
            if (requestId !== this.linkLookupRequestId) {
              return;
            }
            this.isSearchingLinkContract = false;
            this.linkContractError = this.getGraphQLErrorMessage(
              err,
              `Contract ${displayNumber} does not exist in SCM.`,
            );
            this.cdr.detectChanges();
          },
        }),
    );
  }

  onLinkContractToProfile(): void {
    const contractNumber = this.linkContractNumber?.trim();
    if (!this.selectedUser) {
      this.showWarning('Select a user profile first.');
      return;
    }
    if (!contractNumber) {
      this.showWarning('Enter a contract number to link.');
      return;
    }
    if (!this.linkContractMatch || contractNumber !== this.lastSearchedLinkContractNumber) {
      this.showWarning('Search for an exact contract match before linking.');
      return;
    }
    if (this.isSearchedContractInactive) {
      this.showWarning(this.inactiveContractLinkMessage);
      return;
    }
    if (this.isLinkBlockedByAssociation) {
      this.showWarning(this.linkAssociationMessage);
      return;
    }
    if (this.isLinkContractAlreadyLinked) {
      this.showWarning('This contract is already linked to the selected profile.');
      return;
    }
    if (!this.canOfferContractLink) {
      this.showWarning('Search for an exact contract match before linking.');
      return;
    }

    this.isLinkingContract = true;
    // Use the SCM-verified contract number (linkContractMatch), which may be
    // formatted differently (e.g. "NL"-prefixed) than what the user typed.
    this.updateContractLink(
      this.linkContractMatch,
      this.getUserLoginId(this.selectedUser),
      true,
      () => {
        this.resetLinkContractLookup();
        this.isLinkingContract = false;
        this.loadLinkedContracts(this.selectedUser);
        this.loadHistory(this.selectedUser);
      },
      () => {
        this.isLinkingContract = false;
      },
    );
  }

  onUnlinkContract(contract: IContractPortalLinkedContract): void {
    if (!this.selectedUser) {
      return;
    }
    this.updateContractLink(
      contract.contractNumber,
      this.getUserLoginId(this.selectedUser),
      false,
      () => {
        this.loadLinkedContracts(this.selectedUser);
        this.loadHistory(this.selectedUser);
      },
    );
  }

  onSearchByContract(): void {
    const contractNumber = this.contractSearch?.trim();
    if (!contractNumber) {
      this.showWarning('Please enter a contract number to search.');
      return;
    }

    this.isSearchingContractUsers = true;
    this.activeContractNumber = contractNumber;

    this.subscriptions.push(
      this.userProfileTabService
        .getContractLevelUsers(contractNumber, this.azureService.accountId)
        .subscribe({
          next: (users) => {
            this.contractUsers = this.normalizeContractUsers(users);
            this.isSearchingContractUsers = false;
            this.cdr.detectChanges();
          },
          error: (err) => {
            this.isSearchingContractUsers = false;
            this.showError(
              this.getGraphQLErrorMessage(err, 'Failed to load user profiles for this contract.'),
            );
          },
        }),
    );
  }

  onToggleContractUserLink(user: IContractPortalUser): void {
    if (!this.activeContractNumber) {
      return;
    }
    const link = !this.isUserLinked(user);
    this.updateContractLink(
      this.activeContractNumber,
      this.getUserLoginId(user),
      link,
      () => {
        this.onSearchByContract();
      },
    );
  }

  openContract(contractNumber: string): void {
    const trimmed = contractNumber?.trim();
    if (!trimmed || this.openingContractNumber) {
      return;
    }

    this.openingContractNumber = trimmed;

    // SCM stores some contract numbers with an "NL" prefix, so try both the
    // number as shown and the NL-prefixed form until one resolves.
    const candidates = /^nl/i.test(trimmed)
      ? [trimmed, trimmed.replace(/^nl/i, '')]
      : [trimmed, `NL${trimmed}`];

    this.resolveAndOpenContract(trimmed, candidates);
  }

  private resolveAndOpenContract(displayNumber: string, candidates: string[]): void {
    const [current, ...remaining] = candidates;

    if (!current) {
      this.openingContractNumber = '';
      this.showWarning(`Contract ${displayNumber} could not be found.`);
      this.cdr.detectChanges();
      return;
    }

    this.subscriptions.push(
      this.searchContractService
        .getContractSearchResult({
          // Empty status object => search across ALL statuses (matches the
          // behaviour of the normal contract search; omitting it returns none).
          status: { key: '', value: '' },
          contractNumber: current,
          limit: 10,
          offset: 0,
          azureUserId: this.azureService.accountId,
        })
        .pipe(
          filter((result: any) => !result?.loading && !!result?.data),
          take(1),
        )
        .subscribe({
          next: ({ data }) => {
            const rows = data?.getContractSearchResults ?? [];
            const match = this.pickContractMatch(rows, displayNumber, current);

            if (!match) {
              // Try the next candidate form (e.g. NL-prefixed) before giving up.
              this.resolveAndOpenContract(displayNumber, remaining);
              return;
            }

            this.openingContractNumber = '';
            const tab: Tab = {
              header: `Contract [ ${match.contractNumber || displayNumber} ]`,
              content: match,
              type: 'Contract',
            };
            this.tabsService.createTab(tab);
            this.cdr.detectChanges();
          },
          error: (err) => {
            this.openingContractNumber = '';
            this.showError(this.getGraphQLErrorMessage(err, 'Failed to open the contract.'));
            this.cdr.detectChanges();
          },
        }),
    );
  }

  private pickContractMatch(rows: any[], displayNumber: string, current: string): any {
    if (!rows?.length) {
      return null;
    }
    const target = this.normalizeContractNumber(displayNumber);
    const currentTarget = this.normalizeContractNumber(current);
    const exact = rows.find((row) => {
      const number = this.normalizeContractNumber(row.contractNumber);
      return (
        number === target ||
        number === `nl${target}` ||
        number === currentTarget ||
        number === `nl${currentTarget}`
      );
    });
    // Fall back to the single result if the backend returned exactly one row.
    return exact ?? (rows.length === 1 ? rows[0] : null);
  }

  onShowHistory(user: IContractPortalUserProfile | IContractPortalUser): void {
    this.historyUser = user;
    this.displayHistory = true;
    this.loadDialogHistory(user);
  }

  onCloseHistory(): void {
    this.displayHistory = false;
    this.historyUser = null;
    this.dialogHistoryItems = [];
  }

  getUserDisplayName(user: IContractPortalUserProfile | IContractPortalUser): string {
    const profile = user as IContractPortalUserProfile;
    if (profile.firstName || profile.lastName) {
      return [profile.firstName, profile.lastName].filter(Boolean).join(' ');
    }
    const username = (user as IContractPortalUser).username?.trim();
    return profile.name?.trim() || username || '—';
  }

  getUserEmail(user: IContractPortalUserProfile | IContractPortalUser): string {
    return user.emailAddress?.trim() || '—';
  }

  getUserInitials(user: IContractPortalUserProfile | IContractPortalUser): string {
    const profile = user as IContractPortalUserProfile;
    if (profile.firstName && profile.lastName) {
      return `${profile.firstName.charAt(0)}${profile.lastName.charAt(0)}`.toUpperCase();
    }
    const name = this.getUserDisplayName(user);
    if (!name || name === '—') {
      return (user.emailAddress?.charAt(0) || '?').toUpperCase();
    }
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  getUserLoginId(user: IContractPortalUserProfile | IContractPortalUser): number {
    return user.userLoginId ?? (user as IContractPortalUser).usersId;
  }

  isUserSelected(user: IContractPortalUserProfile): boolean {
    return (
      !!this.selectedUser &&
      this.getUserLoginId(this.selectedUser) === this.getUserLoginId(user)
    );
  }

  isUserLinked(user: IContractPortalUser): boolean {
    return this.coerceLink(user.isLinked ?? user.link);
  }

  isContractLinkPending(contractNumber: string): boolean {
    if (!this.selectedUser || !contractNumber) {
      return false;
    }
    return this.pendingLinkKey === this.linkActionKey(contractNumber, this.getUserLoginId(this.selectedUser));
  }

  isUserLinkPending(user: IContractPortalUser): boolean {
    if (!this.activeContractNumber) {
      return false;
    }
    return this.pendingLinkKey === this.linkActionKey(this.activeContractNumber, this.getUserLoginId(user));
  }

  private linkActionKey(contractNumber: string, userLoginId: number): string {
    return `${contractNumber}:${userLoginId}`;
  }

  formatHistoryDate(dateStr: string): string {
    if (!dateStr) {
      return '—';
    }
    return dateTimeFormatterLocal(dateStr);
  }

  getHistoryActionLabel(link: boolean): string {
    return link ? 'Linked' : 'Unlinked';
  }

  trackByUserLoginId = (
    index: number,
    user: IContractPortalUserProfile | IContractPortalUser,
  ): string | number => {
    const id = user?.userLoginId ?? (user as IContractPortalUser)?.usersId;
    return id != null && !Number.isNaN(id) ? id : `row-${index}`;
  };

  trackByContractNumber = (index: number, contract: IContractPortalLinkedContract): string => {
    return contract.contractNumber || `contract-${index}`;
  };

  trackByHistoryIndex = (index: number): number => index;

  private loadLinkedContracts(user: IContractPortalUserProfile): void {
    this.isLoadingLinkedContracts = true;
    this.subscriptions.push(
      this.userProfileTabService
        .getPortalUserProfileContractHistory(
          { userLoginId: this.getUserLoginId(user) },
          this.azureService.accountId,
        )
        .subscribe({
          next: ({ items }) => {
            this.linkedContracts = this.deriveLinkedContracts(items);
            this.isLoadingLinkedContracts = false;
            this.cdr.detectChanges();
          },
          error: (err) => {
            this.isLoadingLinkedContracts = false;
            this.showError(this.getGraphQLErrorMessage(err, 'Failed to load linked contracts.'));
          },
        }),
    );
  }

  private loadHistory(user: IContractPortalUserProfile | IContractPortalUser): void {
    this.isLoadingHistory = true;
    this.historyItems = [];
    this.subscriptions.push(
      this.userProfileTabService
        .getPortalUserProfileContractHistory(
          { userLoginId: this.getUserLoginId(user) },
          this.azureService.accountId,
        )
        .subscribe({
          next: ({ items }) => {
            this.historyItems = [...(items ?? [])].sort(
              (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
            );
            this.isLoadingHistory = false;
            this.cdr.detectChanges();
          },
          error: (err) => {
            this.isLoadingHistory = false;
            this.showError(this.getGraphQLErrorMessage(err, 'Failed to load link history.'));
          },
        }),
    );
  }

  private loadDialogHistory(user: IContractPortalUserProfile | IContractPortalUser): void {
    this.isLoadingDialogHistory = true;
    this.dialogHistoryItems = [];
    this.subscriptions.push(
      this.userProfileTabService
        .getPortalUserProfileContractHistory(
          { userLoginId: this.getUserLoginId(user) },
          this.azureService.accountId,
        )
        .subscribe({
          next: ({ items }) => {
            this.dialogHistoryItems = [...(items ?? [])].sort(
              (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
            );
            this.isLoadingDialogHistory = false;
            this.cdr.detectChanges();
          },
          error: (err) => {
            this.isLoadingDialogHistory = false;
            this.showError(this.getGraphQLErrorMessage(err, 'Failed to load link history.'));
          },
        }),
    );
  }

  private deriveLinkedContracts(
    history: IContractPortalLinkHistoryItem[],
  ): IContractPortalLinkedContract[] {
    if (!history?.length) {
      return [];
    }

    const sorted = [...history].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
    const latestByContract = new Map<string, IContractPortalLinkHistoryItem>();

    sorted.forEach((item) => {
      if (!latestByContract.has(item.contractNumber)) {
        latestByContract.set(item.contractNumber, item);
      }
    });

    return [...latestByContract.values()]
      .filter((item) => item.link)
      .map((item) => ({
        contractNumber: item.contractNumber,
        linkedAt: item.createdAt,
        isLinked: true,
      }));
  }

  private loadLinkAssociationStatus(contractNumber: string, requestId: number): void {
    this.subscriptions.push(
      this.userProfileTabService
        .getContractPortalUsersAssociationStatus(contractNumber, this.azureUserId)
        .subscribe({
          next: (status) => {
            if (requestId !== this.linkLookupRequestId) {
              return;
            }
            this.linkContractMatch = contractNumber;
            this.isSearchingLinkContract = false;
            this.linkAssociation = this.normalizeAssociationStatus(status);
            this.cdr.detectChanges();
          },
          error: (err) => {
            if (requestId !== this.linkLookupRequestId) {
              return;
            }
            this.isSearchingLinkContract = false;
            this.linkContractMatch = '';
            this.linkContractStatus = '';
            this.linkAssociation = null;
            this.linkContractError = this.getGraphQLErrorMessage(
              err,
              'Unable to check whether this contract can be linked.',
            );
            this.cdr.detectChanges();
          },
        }),
    );
  }

  private resetLinkContractLookup(): void {
    this.linkContractNumber = '';
    this.linkContractMatch = '';
    this.linkContractStatus = '';
    this.linkContractError = '';
    this.linkAssociation = null;
    this.isSearchingLinkContract = false;
    this.lastSearchedLinkContractNumber = '';
    this.linkLookupRequestId += 1;
  }

  private updateContractLink(
    contractNumber: string,
    userLoginId: number,
    link: boolean,
    onSuccess?: () => void,
    onComplete?: () => void,
  ): void {
    if (this.isLinkActionInProgress) {
      return;
    }

    this.isLinkActionInProgress = true;
    this.pendingLinkKey = this.linkActionKey(contractNumber, userLoginId);
    this.subscriptions.push(
      this.userProfileTabService
        .updateContractPortalUserLinkStatus(
          contractNumber,
          userLoginId,
          link,
          this.azureUserId,
        )
        .subscribe({
          next: (response) => {
            this.isLinkActionInProgress = false;
            this.pendingLinkKey = '';
            const severity =
              response?.alreadyLinked || response?.alreadyUnlinked ? 'info' : 'success';
            const detail =
              response?.message ||
              (link ? 'Contract linked successfully.' : 'Contract unlinked successfully.');
            this.messageService.add({
              severity,
              summary: response?.alreadyLinked || response?.alreadyUnlinked ? 'No Change' : 'Success',
              detail,
            });
            if (!response?.alreadyLinked && !response?.alreadyUnlinked) {
              onSuccess?.();
            }
            onComplete?.();
            this.cdr.detectChanges();
          },
          error: (err) => {
            this.isLinkActionInProgress = false;
            this.pendingLinkKey = '';
            onComplete?.();
            this.showError(
              this.getGraphQLErrorMessage(
                err,
                link ? 'Failed to link contract.' : 'Failed to unlink contract.',
              ),
            );
          },
        }),
    );
  }

  private normalizePortalUserProfiles(
    users: IContractPortalUserProfile[] | null | undefined,
  ): IContractPortalUserProfile[] {
    if (!users?.length) {
      return [];
    }
    return users.map((user, index) => this.normalizePortalUserProfile(user, index));
  }

  private normalizePortalUserProfile(
    user: IContractPortalUserProfile,
    index: number,
  ): IContractPortalUserProfile {
    const raw = user as IContractPortalUserProfile & Record<string, unknown>;
    const userLoginId = raw.userLoginId ?? (raw['user_login_id'] as number);

    return {
      userProfileId: raw.userProfileId ?? (raw['user_profile_id'] as number),
      userLoginId: userLoginId ?? index,
      firstName: raw.firstName ?? (raw['first_name'] as string),
      lastName: raw.lastName ?? (raw['last_name'] as string),
      name: raw.name ?? (raw['Name'] as string),
      emailAddress:
        raw.emailAddress ??
        (raw['email'] as string) ??
        (raw['email_address'] as string),
    };
  }

  private get azureUserId(): string {
    return this.azureService.accountId || '';
  }

  private normalizeAssociationStatus(
    status: IContractPortalUsersAssociationStatus | null | undefined,
  ): IContractPortalUsersAssociationStatus {
    const raw = (status ?? {}) as IContractPortalUsersAssociationStatus & Record<string, unknown>;
    const associated = this.coerceLink(raw.associated);
    const linkedUsers = raw.linkedUsers ?? (raw['linked_users'] as IContractPortalUser[]);

    return {
      contractNumber: raw.contractNumber ?? (raw['contract_number'] as string),
      associated,
      canLink: this.coerceLink(raw.canLink) && !associated,
      message: (raw.message as string) || '',
      linkedUsers: this.normalizeContractUsers(linkedUsers),
    };
  }

  private normalizeContractUsers(
    users: IContractPortalUser[] | null | undefined,
  ): IContractPortalUser[] {
    if (!users?.length) {
      return [];
    }
    return users.map((user, index) => this.normalizeContractUser(user, index));
  }

  private normalizeContractUser(user: IContractPortalUser, index: number): IContractPortalUser {
    const raw = user as IContractPortalUser & Record<string, unknown>;
    const userLoginId =
      raw.userLoginId ??
      (raw['user_login_id'] as number) ??
      raw.usersId ??
      (raw['users_id'] as number);
    const link = this.coerceLink(raw.link ?? raw.isLinked ?? raw['Link']);
    const emailAddress =
      raw.emailAddress ??
      (raw['email'] as string) ??
      (raw['email_address'] as string);
    const username =
      raw.username ?? (raw['user_name'] as string);
    const name = raw.name ?? (raw['Name'] as string);

    return {
      userLoginId: userLoginId ?? index,
      usersId: userLoginId ?? raw.usersId ?? index,
      name: name ?? null,
      username: username ?? name ?? null,
      emailAddress: emailAddress ?? null,
      link,
      isLinked: link,
    };
  }

  private coerceLink(value: unknown): boolean {
    if (value === true || value === 1 || value === '1' || value === 'true' || value === 'Y') {
      return true;
    }
    if (value === false || value === 0 || value === '0' || value === 'false' || value === 'N') {
      return false;
    }
    return Boolean(value);
  }

  private normalizeContractNumber(contractNumber: string): string {
    return (contractNumber || '').trim().toLowerCase();
  }

  private getGraphQLErrorMessage(err: unknown, fallback: string): string {
    const graphQLError = (err as { graphQLErrors?: { message?: string }[] })?.graphQLErrors?.[0]
      ?.message;
    const message = (err as { message?: string })?.message;
    return graphQLError || message || fallback;
  }

  private showWarning(detail: string): void {
    this.messageService.add({
      severity: 'warn',
      summary: 'Validation',
      detail,
    });
  }

  private showError(detail: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail,
    });
  }
}
